import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Assignment,
  AssignmentSubmission,
  AttendanceCourseSummary,
  Class,
  Quiz,
  Teacher,
} from "../models";
import { getBreaksReportForClass } from "../services/attendance-summary-service";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
}

interface AuthRequest extends Request {
  user?: { userId: string; role: string; schoolId?: string };
}

class TeacherReportsController {
  private async getTeacherContext(userId: string) {
    const teacher = await Teacher.findOne({
      userId: new mongoose.Types.ObjectId(userId),
    });
    if (!teacher) return null;

    const classFilter: Record<string, unknown> = {
      teacherIds: teacher._id,
      schoolId: teacher.schoolId,
    };
    return { teacher, classFilter };
  }

  async getOverview(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, semesterId } = req.query;
      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const ctx = await this.getTeacherContext(userId);
      if (!ctx) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const teacherClassFilter = { ...ctx.classFilter };
      if (classId)
        teacherClassFilter._id = new mongoose.Types.ObjectId(classId as string);
      if (semesterId) {
        teacherClassFilter.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      const classes = await Class.find({ ...teacherClassFilter })
        .select("_id studentIds")
        .lean();
      const classIds = classes.map(c => c._id);
      const studentIds = new Set<string>();
      for (const c of classes) {
        c.studentIds?.forEach(id => {
          studentIds.add(id.toString());
        });
      }

      const [totalAssignments, totalQuizzes, flaggedStudents, summaryAgg] =
        await Promise.all([
          Assignment.countDocuments({ classId: { $in: classIds } }),
          Quiz.countDocuments({ classId: { $in: classIds } }),
          AttendanceCourseSummary.countDocuments({
            classId: { $in: classIds },
            attendanceFlagged: true,
          }),
          AttendanceCourseSummary.aggregate([
            { $match: { classId: { $in: classIds } } },
            {
              $group: {
                _id: null,
                totalClassesHeld: { $sum: "$totalClassesHeld" },
              },
            },
          ]),
        ]);

      res.status(200).json({
        success: true,
        message: "Report overview retrieved successfully",
        data: {
          totalStudents: studentIds.size,
          totalAssignments,
          totalQuizzes,
          totalClassesHeld: summaryAgg[0]?.totalClassesHeld ?? 0,
          flaggedStudents,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher report overview error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve report overview",
      } as APIResponse);
    }
  }

  async getGrades(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, semesterId } = req.query;
      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const ctx = await this.getTeacherContext(userId);
      if (!ctx) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const teacherClassFilter = { ...ctx.classFilter };
      if (classId)
        teacherClassFilter._id = new mongoose.Types.ObjectId(classId as string);
      if (semesterId) {
        teacherClassFilter.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      const classes = await Class.find({ ...teacherClassFilter })
        .select("_id className")
        .lean();
      const classIds = classes.map(c => c._id);

      const assignments = await Assignment.find({ classId: { $in: classIds } })
        .select("id title classId totalMarks status")
        .lean();

      res.status(200).json({
        success: true,
        message: "Grades report retrieved successfully",
        data: {
          classes: classes.map(c => ({
            classId: c._id?.toString(),
            className: c.className,
          })),
          assignments: assignments.map(a => ({
            id: a._id?.toString(),
            title: a.title,
            classId: a.classId?.toString(),
            totalMarks: a.totalMarks,
            status: a.status,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher grades report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve grades report",
      } as APIResponse);
    }
  }

  async getLateSubmissions(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.query;
      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const ctx = await this.getTeacherContext(userId);
      if (!ctx) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const teacherClassFilter = { ...ctx.classFilter };
      if (classId)
        teacherClassFilter._id = new mongoose.Types.ObjectId(classId as string);

      const classes = await Class.find({ ...teacherClassFilter })
        .select("_id className")
        .lean();
      const classIds = classes.map(c => c._id);

      const submissions = await AssignmentSubmission.find({
        classId: { $in: classIds },
        isLateSubmission: true,
      })
        .populate("studentId", "fullName studentId profilePicture")
        .populate("assignmentId", "title deadline totalMarks")
        .sort({ submittedAt: -1 })
        .limit(100)
        .lean();

      res.status(200).json({
        success: true,
        message: "Late submissions report retrieved successfully",
        data: {
          records: submissions.map(s => ({
            id: s._id?.toString(),
            classId: s.classId?.toString(),
            student: s.studentId,
            assignment: s.assignmentId,
            submittedAt: s.submittedAt,
            marksObtained: s.marksObtained,
            isLateSubmission: s.isLateSubmission,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher late submissions report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve late submissions report",
      } as APIResponse);
    }
  }

  async getAttendanceReport(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.query;
      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const ctx = await this.getTeacherContext(userId);
      if (!ctx) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      if (!classId) {
        res.status(400).json({
          success: false,
          message: "classId query parameter is required",
        });
        return;
      }

      const classObjectId = new mongoose.Types.ObjectId(classId as string);
      const classItem = await Class.findOne({
        ...ctx.classFilter,
        _id: classObjectId,
      }).lean();
      if (!classItem) {
        res.status(404).json({ success: false, message: "Class not found" });
        return;
      }

      const summaries = await AttendanceCourseSummary.find({
        classId: classObjectId,
      })
        .populate("studentId", "fullName studentId profilePicture")
        .lean();

      const breaksReport = await getBreaksReportForClass(classObjectId);

      res.status(200).json({
        success: true,
        message: "Attendance report retrieved successfully",
        data: {
          classId,
          className: classItem.className,
          summaries: summaries.map(s => ({
            studentId: s.studentId,
            totalClassesHeld: s.totalClassesHeld,
            classesAttended: (s.classesPresent ?? 0) + (s.classesLate ?? 0),
            classesPresent: s.classesPresent,
            classesLate: s.classesLate,
            classesAbsent: s.classesAbsent,
            attendancePercentage: s.attendancePercentage,
            attendanceFlagged: s.attendanceFlagged,
            flaggedAt: s.flaggedAt,
          })),
          breaksReport,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher attendance report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve attendance report",
      } as APIResponse);
    }
  }
}

export const teacherReportsController = new TeacherReportsController();
